import { useCallback, useEffect, useState } from "react";

type AnimationState = {
  showFirstText: boolean;
  showSecondText: boolean;
  showThirdText: boolean;
  complete: boolean;
};

type OnboardingAnimationConfig = {
  isActive?: boolean;
  hasThirdText?: boolean;
  skipFirstText?: boolean;
};

/**
 * Hook for managing onboarding step animations with proper orchestration
 *
 * This hook abstracts the common animation pattern used across onboarding steps:
 * 1. First text types out (500ms delay)
 * 2. Second text types out (300ms after first completes)
 * 3. Third text types out (300ms after second completes) - optional
 * 4. UI elements fade in (500ms after last text completes)
 *
 * Usage:
 * ```tsx
 * // For steps with 3 text elements (default)
 * const { animationState, handleFirstTextComplete, handleSecondTextComplete, handleThirdTextComplete } =
 *   useOnboardingAnimation({ isActive });
 *
 * // For steps with only 2 text elements
 * const { animationState, handleFirstTextComplete, handleSecondTextComplete } =
 *   useOnboardingAnimation({ isActive, hasThirdText: false });
 * ```
 *
 * Benefits:
 * - Eliminates ~30 lines of repetitive code per step
 * - Ensures consistent timing across all steps
 * - Prevents animation overlap and infinite loops
 * - Centralized animation logic for easy maintenance
 */
export const useOnboardingAnimation = ({
  isActive = false,
  hasThirdText = true,
  skipFirstText = false,
}: OnboardingAnimationConfig) => {
  const [animationState, setAnimationState] = useState<AnimationState>({
    showFirstText: false,
    showSecondText: false,
    showThirdText: false,
    complete: false,
  });

  // Start the animation sequence when component becomes active
  useEffect(() => {
    if (!isActive) return;

    if (skipFirstText) {
      // Skip first text and go directly to second text
      const startSecondText = setTimeout(() => {
        setAnimationState((prev) => ({ ...prev, showSecondText: true }));
      }, 500);
      return () => clearTimeout(startSecondText);
    } else {
      const startFirstText = setTimeout(() => {
        setAnimationState((prev) => ({ ...prev, showFirstText: true }));
      }, 500);
      return () => clearTimeout(startFirstText);
    }
  }, [isActive, skipFirstText]);

  const handleFirstTextComplete = useCallback(() => {
    setTimeout(() => {
      setAnimationState((prev) => ({ ...prev, showSecondText: true }));
    }, 300);
  }, []);

  const handleSecondTextComplete = useCallback(() => {
    if (hasThirdText) {
      setTimeout(() => {
        setAnimationState((prev) => ({ ...prev, showThirdText: true }));
      }, 300);
    } else {
      // Skip third text and go straight to UI
      setTimeout(() => {
        setAnimationState((prev) => ({ ...prev, complete: true }));
      }, 300);
    }
  }, [hasThirdText]);

  const handleThirdTextComplete = useCallback(() => {
    setTimeout(() => {
      setAnimationState((prev) => ({ ...prev, complete: true }));
    }, 500);
  }, []);

  return {
    animationState,
    handleFirstTextComplete,
    handleSecondTextComplete,
    handleThirdTextComplete,
  };
};
